import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, Box } from '@material-ui/core';
import { PasslySelect, ModalActionsComponent, PasslySwitch, PasslyTextField } from '@app/components';
import {
    getModalSwitch,
    getLoaderBtn,
    handleGetPoliciesAuthData,
    handleIsPrincipalNameAvailable,
    handleGetProvisioningPoliciesDTOData,
    addUserData,
    loadingData
} from '@app/store/actions';
import { fetchPoliciesSelectLimit, onPressEnterModal, genericErrorString, useDebounce, userDisplayNameRegex } from '@app/helpers';
import { IPolicyAuth, Order } from '@app/models';
import { useTypedTranslation } from '@app/hooks';
import { AxiosResponse } from 'axios';
import { usersService } from '@app/services';

const validator = require('validator');
const passwordValidator = require('password-validator');

export interface IModalFormData {
    displayName?: string;
    emailAddress?: string;
    username?: string;
    selectValue?: any[];
    password?: string;
    passwordConfirm?: string;
    handleRefresh: Function;
}

export default function AddUserModalForm(props: IModalFormData) {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const policiesData: IPolicyAuth[] = useSelector((state: any) => state.provisioningPolicies.provisioningPoliciesDTO);
    const organizationId = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const isUsernameTaken = useSelector((state: any) => state.users.principalName);

    const [displayName, setDisplayName] = useState('');
    const [emailAddress, setEmailAddress] = useState('');
    const [username, setUsername] = useState('');
    const [selectProvPolicyValue, setProvPolicySelectValue] = useState<string>('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [switcher, setSwitcher] = useState(true);
    const [attributesProp, setAttributesProp] = useState<any[]>([]);
    const [addUserErrorMessage, setAddUserErrorMessage] = useState('');

    const [displayNameValidation, setDisplayNameValidation] = useState(false);
    const [emailAddressValidation, setEmailAddressValidation] = useState(false);
    const [usernameValidation, setUsernameValidation] = useState(false);
    const [passwordValidationIsEmpty, setPasswordValidationIsEmpty] = useState(false);
    const [passwordValidationMinLength, setPasswordValidationMinLength] = useState(false);
    const [passwordValidationUppercase, setPasswordValidationUppercase] = useState(false);
    const [passwordValidationLowercase, setPasswordValidationLowercase] = useState(false);
    const [passwordValidationDigits, setPasswordValidationDigits] = useState(false);
    const [passwordValidationSymbols, setPasswordValidationSymbols] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState(false);
    const [passwordConfirmValidation, setPasswordConfirmValidation] = useState(false);
    const [usernameTaken, setUsernameTaken] = useState(false);
    const [addUserBtn, setAddUserBtn] = useState(true);

    const debouncedUsername = useDebounce(username, 100);
    const debouncedError = useDebounce(isUsernameTaken.value, 100);

    useEffect(() => {
        dispatch(handleGetProvisioningPoliciesDTOData());
    }, []);

    useEffect(() => {
        if (policiesData && policiesData.length > 0) {
            setProvPolicySelectValue(policiesData[0].Id);
        }
    }, [policiesData]);

    useEffect(() => {
        switcher
            ? setAttributesProp([
                  {
                      '@odata.type': 'Kaseya.AuthAnvil.Models.IdentityAttribute',
                      Key: 'policyId',
                      Value: selectProvPolicyValue
                  }
              ])
            : setAttributesProp([
                  {
                      '@odata.type': 'Kaseya.AuthAnvil.Models.IdentityAttribute',
                      Key: 'password',
                      Value: password
                  },
                  {
                      '@odata.type': 'Kaseya.AuthAnvil.Models.IdentityAttribute',
                      Key: 'policyId',
                      Value: selectProvPolicyValue
                  }
              ]);
    }, [switcher, selectProvPolicyValue, password]);

    useEffect(() => {
        dispatch(handleIsPrincipalNameAvailable(organizationId, username, null));
    }, [debouncedUsername, organizationId, username, dispatch]);

    useEffect(() => {
        if (!isUsernameTaken.value && username.length) {
            setUsernameTaken(true);
        } else {
            setUsernameTaken(false);
        }
    }, [debouncedError, isUsernameTaken.value, username.length]);

    useEffect(() => {
        dispatch(handleGetPoliciesAuthData('DisplayName', Order.desc, fetchPoliciesSelectLimit, 0, false));
    }, [dispatch]);

    useEffect(() => {
        if (
            !displayNameValidation &&
            !emailAddressValidation &&
            !usernameValidation &&
            !passwordValidation &&
            !passwordConfirmValidation &&
            !switcher &&
            !usernameTaken &&
            displayName.length &&
            emailAddress.length &&
            username.length &&
            password.length &&
            passwordConfirm.length
        ) {
            setAddUserBtn(false);
        } else if (
            !displayNameValidation &&
            !emailAddressValidation &&
            !usernameValidation &&
            switcher &&
            displayName.length &&
            emailAddress.length &&
            username.length &&
            !usernameTaken
        ) {
            setAddUserBtn(false);
        } else {
            setAddUserBtn(true);
        }
    }, [
        displayName,
        emailAddress,
        username,
        password,
        passwordConfirm,
        switcher,
        usernameTaken,
        displayNameValidation,
        emailAddressValidation,
        passwordValidation,
        usernameValidation,
        passwordConfirmValidation
    ]);

    const handleAddUser = (
        odataType: string,
        provisioningState: string,
        organizationId: string,
        emailAddress: string,
        displayName: string,
        principalName: string,
        attributes: any[],
        handleError: Function
    ) => {
        usersService
            .addUser(odataType, provisioningState, organizationId, emailAddress.trim(), displayName.trim(), principalName, attributes, handleError)
            .then((response: AxiosResponse) => {
                dispatch(addUserData(response.data));
                dispatch(loadingData(false));
            })
            .then(() => {
                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));
                props.handleRefresh(true);
            })
            .catch(() => {
                dispatch(loadingData(false));
            });
    };

    const handleAddDisplayName = (e: any) => {
        const displayName: string = e.target.value;
        setDisplayName(displayName);

        let regExp = new RegExp(userDisplayNameRegex);

        if (!validator.isEmpty(displayName) || regExp.test(displayName)) {
            setDisplayNameValidation(false);
        } else {
            setDisplayNameValidation(true);
        }
    };

    const handleAddEmailAddress = (e: any) => {
        setEmailAddress(e.target.value);
        setUsername(e.target.value);

        if (validator.isEmail(e.target.value) && validator.isLength(e.target.value, { min: 5 })) {
            setEmailAddressValidation(false);
        } else {
            setEmailAddressValidation(true);
        }
        if (username === emailAddress) {
            setUsernameValidation(false);
        }
    };

    const handleAddUsername = (e: any) => {
        setUsername(e.target.value);

        if (!validator.isEmpty(e.target.value)) {
            setUsernameValidation(false);
        } else if (username === emailAddress) {
            setUsernameValidation(false);
        } else {
            setUsernameValidation(true);
        }
    };

    const handleProvPolicySelectValue = (e: any) => {
        setProvPolicySelectValue(e.target.value);
    };

    const handlePassword = (e: any) => {
        const schemaUppercase = new passwordValidator();
        const schemaLowercase = new passwordValidator();
        const schemaDigits = new passwordValidator();
        const schemaSymbols = new passwordValidator();

        schemaUppercase.has().uppercase();
        schemaLowercase.has().lowercase();
        schemaDigits.has().digits();
        schemaSymbols.has().symbols();

        setPassword(e.target.value);

        if (!validator.isEmpty(e.target.value)) {
            setPasswordValidationIsEmpty(false);
        } else {
            setPasswordValidationIsEmpty(true);
        }
        if (validator.isLength(e.target.value, { min: 6 })) {
            setPasswordValidationMinLength(false);
        } else {
            setPasswordValidationMinLength(true);
        }
        if (schemaUppercase.validate(e.target.value)) {
            setPasswordValidationUppercase(false);
        } else {
            setPasswordValidationUppercase(true);
        }
        if (schemaLowercase.validate(e.target.value)) {
            setPasswordValidationLowercase(false);
        } else {
            setPasswordValidationLowercase(true);
        }
        if (schemaDigits.validate(e.target.value)) {
            setPasswordValidationDigits(false);
        } else {
            setPasswordValidationDigits(true);
        }
        if (schemaSymbols.validate(e.target.value)) {
            setPasswordValidationSymbols(false);
        } else {
            setPasswordValidationSymbols(true);
        }
        if (
            !validator.isEmpty(e.target.value) &&
            validator.isLength(e.target.value, { min: 6 }) &&
            schemaUppercase.validate(e.target.value) &&
            schemaLowercase.validate(e.target.value) &&
            schemaDigits.validate(e.target.value) &&
            schemaSymbols.validate(e.target.value)
        ) {
            setPasswordValidation(false);
        } else {
            setPasswordValidation(true);
        }
    };

    const handlePasswordConfirm = (e: any) => {
        setPasswordConfirm(e.target.value);
        if (!validator.isEmpty(e.target.value) && password === e.target.value) {
            setPasswordConfirmValidation(false);
        } else {
            setPasswordConfirmValidation(true);
        }
    };

    const handleSwitchChange = () => {
        setSwitcher(!switcher);
        setPassword('');
        setPasswordConfirm('');
    };

    const handleAddUserError = (err: any) => {
        setAddUserErrorMessage(genericErrorString(err));
        setAddUserBtn(false);
        dispatch(getLoaderBtn(false));
    };

    const handleSubmitBtn = () => {
        setAddUserBtn(true);
        dispatch(getLoaderBtn(true));
        if (attributesProp.length) {
            handleAddUser(
                'Kaseya.AuthAnvil.Models.Person',
                'Created',
                organizationId,
                emailAddress,
                displayName,
                username,
                attributesProp,
                handleAddUserError
            );
        }

        return false;
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
    };
    return (
        <>
            <form className='modal__form' method='post' tabIndex={0} onKeyPress={onPressEnterModal(handleSubmitBtn, addUserBtn)}>
                <FormControl>
                    <PasslyTextField
                        fullWidth
                        label={'DISPLAY_NAME'}
                        autoComplete={translation('DISPLAY_NAME')}
                        value={displayName}
                        onChange={handleAddDisplayName}
                        id='displayName-input'
                        autoFocus
                        required
                        error={displayNameValidation}
                        helperText={displayNameValidation && translation('INVALID_DISPLAY_NAME')}
                    />
                </FormControl>
                <FormControl>
                    <PasslyTextField
                        fullWidth
                        label={'EMAIL_ADDR'}
                        autoComplete={translation('EMAIL_ADDR')}
                        value={emailAddress}
                        onChange={handleAddEmailAddress}
                        id='emailAddress-input'
                        required
                        error={emailAddressValidation}
                        helperText={emailAddressValidation && translation('INVALID_EMAIL')}
                    />
                </FormControl>
                <FormControl>
                    <PasslyTextField
                        fullWidth
                        label={'USERNAME'}
                        autoComplete={translation('USERNAME')}
                        value={username}
                        onChange={handleAddUsername}
                        id='username-input'
                        required
                        error={usernameTaken || usernameValidation}
                        helperText={
                            (usernameTaken && translation('INVALID_TAKEN_PRINCIPAL_NAME')) ||
                            (usernameValidation && translation('INVALID_PRINCIPAL_NAME'))
                        }
                    />
                </FormControl>
                <FormControl>
                    <PasslySelect
                        value={selectProvPolicyValue}
                        options={policiesData}
                        optionLabel='DisplayName'
                        optionValue='Id'
                        onChange={handleProvPolicySelectValue}
                        id='select-input'
                        label={'PROVISIONING_POLICY'}
                        appendTo={document.body}
                    />
                </FormControl>
                <FormControl>
                    <PasslySwitch checked={switcher} onChange={handleSwitchChange} label={translation('LET_US_GENERATE_PASSWORD')} />
                </FormControl>
                {switcher ? (
                    ''
                ) : (
                    <FormControl>
                        <PasslyTextField
                            fullWidth
                            label={'PASSWORD'}
                            value={password}
                            onChange={handlePassword}
                            id='addUserPassword-input'
                            type='password'
                            autoFocus
                            required
                            error={passwordValidation}
                            helperText={
                                (passwordValidationIsEmpty && translation('PASSWORD_SERVER_POLICY_MINIMUM_LENGTH')) ||
                                (passwordValidationMinLength && translation('PASSWORD_SERVER_POLICY_MINIMUM_LENGTH')) ||
                                (passwordValidationUppercase && translation('PASSWORD_CRITERIA_UPPERCASE')) ||
                                (passwordValidationLowercase && translation('PASSWORD_CRITERIA_LOWERCASE')) ||
                                (passwordValidationDigits && translation('PASSWORD_CRITERIA_DIGIT')) ||
                                (passwordValidationSymbols && translation('PASSWORD_SERVER_POLICY_REQUIRE_SYMBOL'))
                            }
                        />
                    </FormControl>
                )}
                {switcher ? (
                    ''
                ) : (
                    <FormControl>
                        <PasslyTextField
                            fullWidth
                            label={'CONFIRM_PASSWORD'}
                            value={passwordConfirm}
                            onChange={handlePasswordConfirm}
                            id='addUserPasswordConfirm-input'
                            type='password'
                            required
                            error={passwordConfirmValidation}
                            helperText={passwordConfirmValidation && translation('PASSWORDS_DONT_MATCH')}
                        />
                    </FormControl>
                )}
                {addUserErrorMessage !== '' && (
                    <p>
                        <div className='modal__error'>{addUserErrorMessage}</div>
                    </p>
                )}
            </form>
            <ModalActionsComponent
                primaryBtn={translation('ADD_USER')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSubmitBtn}
                onClickSecondary={handleCancel}
                disabled={addUserBtn}
                id='addUser'
            />
        </>
    );
}
